import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Checkbox, Input, Label, Textarea } from '@design-system';
import { selectSessionAdvisor } from '@client/model/selectors';
import { useRenderedIntakeEmail } from '../hooks/use-rendered-intake-email';

function EmailFrame({ title, html }: { title: string; html: string }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <div className="text-sm font-medium text-gray-700">{title}</div>
      <iframe
        title={title}
        srcDoc={html}
        className="h-[700px] w-full rounded-md border border-gray-200 bg-white"
        sandbox=""
      />
    </div>
  );
}

export function EmailTemplatePreview() {
  const advisor = useSelector(selectSessionAdvisor);
  const [advisorFirstName, setAdvisorFirstName] = useState('Edward');
  const [clientFirstName, setClientFirstName] = useState('Alex');
  const [advisorProvidedBody, setAdvisorProvidedBody] = useState('');
  const [withFooter, setWithFooter] = useState(true);
  const [compareLegacy, setCompareLegacy] = useState(true);
  const [withSignature, setWithSignature] = useState(true);

  const { reactEmailHtml, legacyHtml } = useRenderedIntakeEmail(
    {
      advisorFirstName,
      clientFirstName,
      advisorProvidedBody: advisorProvidedBody || undefined,
      advisorSignatureHtml: withSignature ? advisor?.emailSignature : undefined,
      intakeFormLink: 'https://example.com/onboarding/preview',
    },
    withFooter,
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="advisor-first-name">Advisor first name</Label>
          <Input
            id="advisor-first-name"
            value={advisorFirstName}
            onChange={e => setAdvisorFirstName(e.target.value)}
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="client-first-name">Client first name</Label>
          <Input
            id="client-first-name"
            value={clientFirstName}
            onChange={e => setClientFirstName(e.target.value)}
          />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="personalized-message">
          Personalized message (blank uses the default copy)
        </Label>
        <Textarea
          id="personalized-message"
          rows={3}
          value={advisorProvidedBody}
          onChange={e => setAdvisorProvidedBody(e.target.value)}
          placeholder="Add a personal message to the invitation email..."
        />
      </div>
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <Checkbox
            id="with-footer"
            checked={withFooter}
            onCheckedChange={value => setWithFooter(value === true)}
          />
          <Label htmlFor="with-footer">Show unsubscribe footer</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="compare-legacy"
            checked={compareLegacy}
            onCheckedChange={value => setCompareLegacy(value === true)}
          />
          <Label htmlFor="compare-legacy">Compare with legacy template</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="with-signature"
            checked={withSignature}
            onCheckedChange={value => setWithSignature(value === true)}
          />
          <Label htmlFor="with-signature">Use my saved email signature</Label>
        </div>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        <EmailFrame
          title="React Email (current send path)"
          html={reactEmailHtml}
        />
        {compareLegacy && (
          <EmailFrame
            title="Legacy string template (pre-migration)"
            html={legacyHtml}
          />
        )}
      </div>
    </div>
  );
}
